import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Store } from 'lucide-react';
import { cashierLogin, ensureCashierDeviceRegistered } from '../services/cashierApi';
import { useCashierAuth } from '../hooks/useCashierAuth';
import { getOrCreateDeviceFingerprint } from '../utils/deviceFingerprint';

export default function CashierLoginPage() {
  const { isAuthenticated, login } = useCashierAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/';

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    if (!identifier.trim() || !password) {
      toast.error('identifier, password required');
      return;
    }
    setSubmitting(true);
    try {
      const deviceFingerprint = getOrCreateDeviceFingerprint();
      const res = await cashierLogin({
        identifier: identifier.trim(),
        password,
        deviceFingerprint,
      });
      if (!res.success || !res.data?.accessToken || !res.data?.cashier) {
        toast.error(res.message || 'Sign in failed');
        return;
      }

      login(res.data.cashier, res.data.accessToken);

      const reg = await ensureCashierDeviceRegistered();
      if (!reg.ok) {
        toast.error(reg.message || 'Device registration failed. Ask shop manager to check Devices.');
      } else {
        toast.success('Signed in — device registered for approval');
      }

      navigate(from, { replace: true });
    } catch (e: unknown) {
      toast.error((e as any)?.response?.data?.message || 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-amber-50 via-white to-emerald-50 px-3 py-6 sm:flex sm:items-center sm:justify-center sm:px-4 sm:py-10">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-600 text-white shadow-lg">
            <Store className="h-8 w-8" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Cashier</h1>
          <p className="mt-1 text-sm text-gray-600">Sign in is separate from the Shop Admin portal</p>
        </div>

        <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-xl shadow-amber-100/50 sm:p-8">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identifier</label>
              <input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="w-full rounded-lg border border-gray-200 py-2.5 px-3 text-base focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
                placeholder="username, email or phone"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                className="w-full rounded-lg border border-gray-200 py-2.5 px-3 text-base focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <button
              type="button"
              onClick={() => void onSubmit()}
              disabled={submitting}
              className="w-full rounded-lg bg-amber-600 py-2.5 text-sm font-semibold text-white shadow hover:bg-amber-700 disabled:opacity-60 transition-colors"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
